import React, { useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_learning_tracks } from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { describeDefaultDueDate } from "./dueDateHelpers";

// Assignable items can be a full track or a single directly-assignable asset
// (the wrapper carries assignment_type). Used to make the modal copy match
// what's actually being assigned.
const TYPE_LABELS = {
  track: "Track",
  module: "Module",
  section: "Section",
  quiz: "Quiz",
  swipe_deck: "Swipe Deck",
  form: "Form",
  roleplay: "Practice Scenario",
};
const typeLabelFor = (assignmentType) =>
  TYPE_LABELS[assignmentType] || "Item";

function AssignTrackToEmployeeModal(props) {
  const [selectedTrack, setSelectedTrack] = React.useState("");
  const [selectedDueDate, setSelectedDueDate] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [learningTracks, setLearningTracks] = React.useState(null);

  const availableLearningTracks = React.useMemo(() => {
    const assignedTrackUuids = new Set(
      (props.selectedEmployee?.user?.learning_track_enrollments || [])
        .map((enrollment) => enrollment?.track?.uuid)
        .filter(Boolean)
    );

    return (learningTracks || []).filter(
      (track) =>
        !assignedTrackUuids.has(track?.uuid) &&
        // Draft tracks aren't assignable; only published/legacy (NULL) show.
        track?.status !== "draft"
    );
  }, [learningTracks, props.selectedEmployee]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    maxHeight: "90vh",
    bgcolor: "background.paper",
    borderRadius: "16px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    overflowY: "auto",
    border: "none",
  };

  const handleDropdownTrackSelect = (track) => {
    setSelectedTrack(track);
  };

  const fetchLearningTracks = async (showLoading) => {
    setIsLoading(showLoading);
    let api_params = {
      company_id: props.homeCompany,
    };
    try {
      const res = await getRightworkAPI(API_get_learning_tracks, api_params);
      if (res.status === 200) {
        setLearningTracks(res.data.redux?.payload?.learning_tracks);
        setIsLoading(false);
      }
    } catch (error) {
      throw Error(error);
    }
  };

  React.useEffect(() => {
    fetchLearningTracks(true);
  }, []);

  React.useEffect(() => {
    if (!selectedTrack) return;
    const stillAvailable = availableLearningTracks.some(
      (track) => track.uuid === selectedTrack.uuid
    );
    if (!stillAvailable) {
      setSelectedTrack("");
    }
  }, [availableLearningTracks, selectedTrack]);

  // When opened from a specific track column's "+", pre-select that track.
  // Applied once per open (after tracks load) so the manager can still change it.
  const preselectAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (props.open) {
      preselectAppliedRef.current = false;
    }
  }, [props.open]);
  React.useEffect(() => {
    if (!props.open || preselectAppliedRef.current) return;
    if (!props.preselectedTrackUuid) return;
    const match = availableLearningTracks.find(
      (track) => track?.uuid === props.preselectedTrackUuid
    );
    if (match) {
      setSelectedTrack(match);
      preselectAppliedRef.current = true;
    }
  }, [props.open, props.preselectedTrackUuid, availableLearningTracks]);

  const handleClose = () => {
    setSelectedTrack("");
    setSelectedDueDate(null);
    props.handleClose();
  };

  // The type of the currently selected item ("Track", "Form", "Swipe Deck", …)
  // so the copy reflects what's actually being assigned.
  const selectedTypeLabel = selectedTrack
    ? typeLabelFor(selectedTrack.assignment_type)
    : null;

  // Describe the selected item's default due date so the manager knows whether
  // a custom date is needed. Direct-assignment wrappers have no default.
  const defaultDueDateHelper = (() => {
    if (!selectedTrack) {
      return "Set a specific due date for this employee. If not set, the default due date will be used.";
    }
    const desc = describeDefaultDueDate(selectedTrack);
    return desc
      ? `${desc} Set a custom date to override it for this employee.`
      : "This has no default due date. Add one here if you want a deadline.";
  })();

  return (
    <Modal
      open={props.open}
      onClose={handleClose}
      aria-labelledby="assign-track-modal-title"
      aria-describedby="assign-track-modal-description"
    >
      <Box sx={style}>
        {!isLoading ? (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 mx-0 mt-0 mb-6 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      {selectedTypeLabel
                        ? `Assign ${selectedTypeLabel}`
                        : "Assign Training"}
                    </h3>
                    <p className="text-sm text-slate-600">
                      Choose what to assign to this employee
                    </p>
                  </div>
                </div>
                <button
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                  onClick={handleClose}
                >
                  <CloseIcon />
                </button>
              </div>
            </div>

            <div className="px-6 space-y-6">
              {/* Employee Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <PersonIcon className="text-violet-600" style={{ fontSize: "18px" }} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-900">Assigning to</h4>
                    <p className="text-lg font-semibold text-gray-900">
                      {props.selectedEmployee?.first_name} {props.selectedEmployee?.last_name}
                    </p>
                  </div>
                </div>
              </div>

              {/* Track Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What to assign *
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Choose from your available tracks and assignable items.
                </p>
                <Select
                  fullWidth
                  value={selectedTrack}
                  displayEmpty
                  onChange={(e) => handleDropdownTrackSelect(e.target.value)}
                  disabled={availableLearningTracks.length === 0}
                  size="small"
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    height: "45px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#6d28d9",
                    },
                  }}
                >
                  <MenuItem disabled value="">
                    <span className="text-gray-500">
                      {availableLearningTracks.length > 0
                        ? "Select something to assign"
                        : "Nothing left to assign"}
                    </span>
                  </MenuItem>
                  {availableLearningTracks?.map((track, index) => (
                    <MenuItem key={index} value={track}>
                      {track.name}
                      {track.assignment_type &&
                        track.assignment_type !== "track" && (
                          <span className="ml-2 text-xs text-slate-400">
                            {typeLabelFor(track.assignment_type)}
                          </span>
                        )}
                    </MenuItem>
                  ))}
                </Select>
                {availableLearningTracks.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Nothing left to assign.
                  </p>
                )}
              </div>

              {/* User-Specific Due Date */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarTodayIcon className="text-gray-500" style={{ fontSize: "18px" }} />
                  <label className="block text-sm font-medium text-gray-700">
                    Custom Due Date (Optional)
                  </label>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  {defaultDueDateHelper}
                </p>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={selectedDueDate}
                    onChange={(newDate) => setSelectedDueDate(newDate)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "Select a custom due date",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            height: "45px",
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#6d28d9",
                            },
                          },
                        },
                      },
                      actionBar: {
                        actions: ['clear', 'accept'],
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>

              {/* Track Preview */}
              {selectedTrack && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center mt-0.5">
                      <SchoolIcon className="text-emerald-600" style={{ fontSize: "18px" }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-emerald-900 mb-1">
                        Selected {selectedTypeLabel}
                      </h4>
                      <p className="text-lg font-semibold text-emerald-800">
                        {selectedTrack.name}
                      </p>
                      {selectedTrack.description && (
                        <p className="text-sm text-emerald-700 mt-2">
                          {selectedTrack.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 mx-0 mb-0 mt-8 z-10">
              <div className="flex items-center justify-end gap-3">
                <button
                  className="px-6 py-2.5 border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-lg font-medium transition-colors"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className={`px-6 py-2.5 rounded-lg font-medium transition-colors ${
                    selectedTrack
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-gray-200 text-gray-400 cursor-not-allowed"
                  }`}
                  onClick={() => {
                    if (selectedTrack) {
                      // Format date as YYYY-MM-DD string (same format as track creation)
                      const dueDateString = selectedDueDate
                        ? `${selectedDueDate.getFullYear()}-${String(selectedDueDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDueDate.getDate()).padStart(2, '0')}`
                        : null;

                      props.post_learning_track_enrollment(
                        selectedTrack.uuid,
                        props.selectedEmployee?.id,
                        dueDateString
                      );
                      handleClose();
                    }
                  }}
                  disabled={!selectedTrack || availableLearningTracks.length === 0}
                >
                  {selectedTypeLabel ? `Assign ${selectedTypeLabel}` : "Assign"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-96">
            <LoadSpinner />
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default AssignTrackToEmployeeModal;
